/**
 * L2 (Eric via Master, 2026-10-29): the line at the foot of each client-portal settings card,
 * "Last changed by X on <date>", with a short history (newest first) behind a History toggle.
 * The page reads the history once (hooks/useSettingsHistory) and reloads it after each save.
 */
import { useId, useState } from 'react';
import type { SettingsHistoryEntry } from '@/lib/portalSettingsHistoryApi';

const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

interface Props {
  /** The card's name, for the History button ("History: Coverage limits"). */
  label: string;
  /** Newest first; undefined while loading. */
  entries: SettingsHistoryEntry[] | undefined;
  unavailable?: boolean;
}

export function CardHistory({ label, entries, unavailable }: Props) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  if (!entries) {
    return unavailable ? <p className="text-xs text-muted-foreground">Change history couldn’t be loaded.</p> : null;
  }
  // A value saved before the history began can sit beside this, so it says "since".
  if (!entries.length) return <p className="text-xs text-muted-foreground">No changes recorded since history began.</p>;
  const [last] = entries;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span>{`Last changed by ${last.by} on ${day(last.at)}`}</span>
        <button
          type="button"
          className="rounded-sm font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((o) => !o)}
        >
          {/* Read as "History: <card>", which starts with the shown words (label in name). */}
          <span className="sr-only">{`${open ? 'Hide history' : 'History'}: ${label}`}</span>
          <span aria-hidden="true">{open ? 'Hide history' : 'History'}</span>
        </button>
      </div>
      {open && (
        <ul id={listId} className="flex flex-col gap-1.5 border-l border-border pl-3">
          {entries.map((e) => (
            <li key={`${e.at}-${e.summary}`} className="text-xs">
              <span className="tabular-nums text-muted-foreground">{when(e.at)}</span>
              <span className="text-muted-foreground"> · {e.by}</span>
              <span className="block text-foreground">{e.summary}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
